<script lang="ts">
	import { page } from "$app/state";
	import Breadcrumb from "#lib/Breadcrumb.svelte";
	import type { LayoutData } from "./$types";
	import KundeserviceBanner from "#lib/KundeserviceBanner.svelte";
	import SavingsIcon from "#lib/assets/SavingsIcon.svelte";
	import PlantIcon from "#lib/assets/PlantIcon.svelte";
	import ShieldIcon from "#lib/assets/ShieldIcon.svelte";
	import ElectricalIcon from "#lib/assets/ElectricalIcon.svelte";
	import { Picture } from "@ishavskraft/shared";
	import DiscountBanner from "./DiscountBanner.svelte";
	import CallMeButton from "#lib/CallMeButton.svelte";
	import CampaignBanner from "./CampaignBanner.svelte";
	import FilterCategories from "./FilterCategories.svelte";
	import { productCategoryMapping } from "#lib/types/products.js";
	import { PortableTextContent } from "@ishavskraft/shared";
	import { sortCriteria, selectedSort } from "./sortState.svelte";
	import { SelectSingle } from "@ishavskraft/shared";

	import sessionValues, { syncSessionValues } from "#lib/sessionValues.js";

	let search = $state("");
	const excludedPages = ["standard-installasjon", "kjopsbetingelser"];

	interface Props {
		data: LayoutData;
		children?: import("svelte").Snippet;
	}

	let { data, children }: Props = $props();

	let { title, image, subtitle, description, additional_content } = $derived(
		data?.document ?? ({} as unknown)
	);
	let filters = productCategoryMapping.map((x) => x.key);
	let showCampaignBanner = $state(false);
	let filterTitle = $derived.by(() => {
		const pathCategory =
			productCategoryMapping.find((a) => a.key === page.url.pathname.split("/").pop())?.value ||
			page.url.pathname.split("/").pop();
		return pathCategory === "nettbutikk" ? "Alle produkter" : pathCategory;
	});

	$effect(() => {
		const storedAt = $sessionValues["mnr_stored_at"];
		const mnr = $sessionValues["mnr"];
		const mnrType = $sessionValues["mnr_type"];

		// Nothing to clear — return early so writing back doesn't retrigger this effect.
		if (!storedAt && !mnr && !mnrType) return;

		const twentyFourHours = 24 * 60 * 60 * 1000;
		const expired = !!storedAt && Date.now() - Number(storedAt) > twentyFourHours;
		const hasSourceParam = !!page.url.searchParams.get("source");

		if (expired || hasSourceParam) {
			syncSessionValues({ mnr: "", mnr_type: "", mnr_stored_at: "" }, { useBrowserStorage: true });
		}
	});

	$effect(() => {
		const banner = data?.props?.banner ?? null;
		const currentPath = page?.url?.pathname?.split("/").pop() ?? "";
		showCampaignBanner =
			banner?.active && (!banner.category || banner.category.includes(currentPath));
		search = page?.url?.search ?? "";
	});
</script>

{#if excludedPages.every((x) => !page.url.pathname.includes(x))}
	<Breadcrumb
		breadcrumbs={page.data.breadcrumbs.filter(
			(c: { label: string; href: string }) => c.label !== "Kategori"
		)}
	/>

	<header class="mb-4 pb-8 px-edge-x">
		<div class="flex flex-col tablet:flex-row gap-x-8 gap-y-4">
			<div>
				<p class="heading-1 text-[--lysning]">Nettbutikk</p>
				<h1 class="heading-2">{title}</h1>
				<p class="text-body max-w-prose">{subtitle}</p>
				<p class="text-body max-w-prose">{description}</p>
			</div>
			{#if image.src}
				<Picture
					class="max-h-100 tablet:w-[50%] object-cover rounded-2xl"
					src={image.src}
					srcset={image.srcset}
					srcsetWebp={image.srcsetWebp}
					alt={image?.alt || ""}
					width={image.asset?.metadata?.dimensions?.width}
					height={image.asset?.metadata?.dimensions?.height}
				/>
			{/if}
		</div>
	</header>

	<div class="py-2 bg-[--skodde] overflow-x-auto">
		{#if filters}
			<div class="mt-4 px-edge-x">
				<FilterCategories {filters} {search} activeFilter={page.url.pathname.split("/").pop()} />
			</div>
		{/if}
	</div>

	<section class="py-2 px-edge-x">
		{#if showCampaignBanner}
			<CampaignBanner campaignBanner={data.props.banner} />
		{/if}
		<DiscountBanner />

		<div class="mb-6 grid grid-cols-[1fr_auto] justify-between items-center">
			<h2 class="text-lead whitespace-nowrap text-ellipsis overflow-x-hidden">
				{filterTitle}
			</h2>

			{#if sortCriteria}
				<SelectSingle
					ariaLabel="Sorter etter"
					bind:value={selectedSort.sort.field}
					onchange={() => {
						selectedSort.sort = sortCriteria.find((x) => x.field === selectedSort.sort.field) || {
							field: "",
							order: "",
							key: "",
						};
					}}
					name="sort"
					class="select whitespace-nowrap"
				>
					{#each sortCriteria as { field } (field)}
						<option value={field}>{field}</option>
					{/each}
				</SelectSingle>
			{/if}
		</div>

		<!-- Produktliste basert på filter -->
		{@render children?.()}
	</section>

	<KundeserviceBanner title="Har du spørsmål om produktene, ta gjerne kontakt med oss" />

	<section class="mx-edge-x text-center">
		<h2 class="text-3xl font-title my-2">Spar strøm med Ishavskraft</h2>
		<p class="my-2 text-body">Det er mange fordeler med å oppgradere hjemmet.</p>

		<ul class="list-none grid tablet:grid-cols-4 gap-6 mt-6 mb-12">
			<li>
				<div class="flex items-center gap-2">
					<span aria-hidden="true"> <SavingsIcon /></span>
					<p class="font-700 text-left tablet:hyphens-manual">Spar strøm</p>
				</div>
				<p class="text-left m-0">Strømforbruket kan reduseres med opptil 21% i året</p>
			</li>
			<li>
				<div class="flex items-center gap-2">
					<span aria-hidden="true"> <PlantIcon /></span>
					<p class="font-700 text-left tablet:hyphens-manual">Spar penger</p>
				</div>
				<p class="text-left m-0">
					Energien utnyttes mer effektivt og du reduserer kostnadene til strøm og nettleie
				</p>
			</li>
			<li>
				<div class="flex items-center gap-2">
					<span aria-hidden="true"> <ShieldIcon /></span>
					<p class="font-700 text-left tablet:hyphens-manual">Øk sikkerheten</p>
				</div>
				<p class="text-left m-0">
					Automatsikringer med jordfeilbryter beskytter deg, og reduserer risikoen for brann
				</p>
			</li>
			<li>
				<div class="flex items-center gap-2">
					<span aria-hidden="true"> <ElectricalIcon /></span>
					<p class="font-700 text-left tablet:hyphens-manual">Beskytt dine apparater</p>
				</div>
				<p class="text-left m-0">
					Ved for eksempel lynnedslag, vil et overspenningsvern beskytte utstyr og apparater
				</p>
			</li>
		</ul>
	</section>

	<CallMeButton rotated />

	{#if additional_content}
		<div class="[--text-body:var(--font-size-sm)]">
			<hr class="mx-edge-x mt-12 mb-16" />

			<PortableTextContent content={additional_content} />
		</div>
	{/if}
{:else}
	{@render children?.()}
{/if}

<style lang="postcss">
	section {
		margin-block: var(--space);
	}
</style>
